/**
 * Reads the token pair an external-client transfer hands off in the URL.
 *
 * The backend now emits them in the **fragment** (`#accessToken=…&refreshToken=…`) rather than the query
 * string. A fragment is the one part of a URL a browser does not send to a server, so the pair no longer lands
 * in the receiving host's access log, in any TLS-terminating proxy / CDN / WAF log on the way in, or in a
 * `Referer`. In the query string it was written to all of those — a 4h access token plus a 1-week refresh
 * token, per hand-off.
 *
 * The query string is still read as a **fallback for one release**, so a partner that has cached an older URL
 * shape keeps working while the backend rolls out. Remove the fallback once the transfer endpoints have been
 * on the fragment form long enough that no caller can still be producing the old one.
 */
export type HandoffTokens = {
  accessToken: string | null;
  refreshToken: string | null;
  /** Which half of the URL the pair came from — `null` when neither carried a complete pair. */
  source: "fragment" | "query" | null;
};

/**
 * @param hash   `location.hash`, with or without its leading `#`
 * @param search `location.search`, with or without its leading `?`
 *
 * Both tokens must be present in the fragment for it to win. A fragment carrying only one of them is treated
 * as absent rather than as a partial answer — otherwise a malformed hand-off would shadow a complete pair
 * sitting in the query string and fail for a reason nobody could see.
 */
export const readExternalClientHandoffTokens = (
  hash?: string | null,
  search?: string | null
): HandoffTokens => {
  const fragmentParams = new URLSearchParams((hash ?? "").replace(/^#/, ""));
  const fragmentAccessToken = fragmentParams.get("accessToken");
  const fragmentRefreshToken = fragmentParams.get("refreshToken");
  if (fragmentAccessToken && fragmentRefreshToken) {
    return {
      accessToken: fragmentAccessToken,
      refreshToken: fragmentRefreshToken,
      source: "fragment",
    };
  }

  const queryParams = new URLSearchParams((search ?? "").replace(/^\?/, ""));
  const queryAccessToken = queryParams.get("accessToken");
  const queryRefreshToken = queryParams.get("refreshToken");
  if (queryAccessToken && queryRefreshToken) {
    return {
      accessToken: queryAccessToken,
      refreshToken: queryRefreshToken,
      source: "query",
    };
  }

  return { accessToken: null, refreshToken: null, source: null };
};
